import { useState, useRef, useEffect, useMemo } from 'react';
import { Smile, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const CATEGORIES: Record<string, { label: string; tab: string; emojis: string[] }> = {
  smileys: { label: 'Smileys & People', tab: '😀', emojis: '😀 😃 😄 😁 😆 😅 🤣 😂 🙂 🙃 😉 😊 😇 🥰 😍 🤩 😘 😗 😚 😙 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🤫 🤔 🤐 🤨 😐 😑 😶 😏 😒 🙄 😬 🤥 😌 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🤮 🤧 🥵 🥶 🥴 😵 🤯 🤠 🥳 😎 🤓 🧐 😕 😟 🙁 ☹ 😮 😯 😲 😳 🥺 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠 🤬 😈 👿 💀 👻 👽 🤖 😺 😸 😹 😻 😼 😽 🙀 😿 😾 🙏 👋 🤚 ✋ 🖖 👌 🤏 ✌ 🤞 🤟 🤘 🤙 👈 👉 👆 👇 ☝ 👍 👎 ✊ 👊 🤛 🤜 👏 🙌 👐 🤲 🤝 ✍ 💪 🦵 🦶 👂 👃 🧠 🦷 👀 👁 👅 👄 👶 🧒 👦 👧 🧑 👨 👩 🧓 👴 👵'.split(' ') },
  nature:  { label: 'Animals & Nature', tab: '🐶', emojis: '🐶 🐱 🐭 🐹 🐰 🦊 🐻 🐼 🐨 🐯 🦁 🐮 🐷 🐽 🐸 🐵 🐔 🐧 🐦 🐤 🦆 🦅 🦉 🦇 🐺 🐗 🐴 🦄 🐝 🐛 🦋 🐌 🐞 🐢 🐍 🐙 🐠 🐬 🐳 🦈 🐊 🦓 🐘 🦒 🐫 🐃 🐂 🐄 🐎 🐖 🐏 🐑 🐐 🦌 🐕 🐩 🐈 🐓 🦃 🦚 🐇 🐁 🐀 🐉 🌵 🎄 🌲 🌳 🌴 🌱 🌿 ☘ 🍀 🎍 🍃 🍂 🍁 🍄 🐚 🌾 💐 🌷 🌹 🌺 🌸 🌼 🌻 🌞 🌝 🌛 🌜 🌚 🌕 🌙 🌎 💫 ⭐ 🌟 ✨ ⚡ 💥 🔥 🌪 🌈 ☀ ⛅ ☁ 🌧 ⛈ ❄ ☃ ⛄ 💨 💧 💦 ☔ 🌊'.split(' ') },
  food:    { label: 'Food & Drink', tab: '🍔', emojis: '🍏 🍎 🍐 🍊 🍋 🍌 🍉 🍇 🍓 🍈 🍒 🍑 🥭 🍍 🥥 🥝 🍅 🍆 🥑 🥦 🥬 🥒 🌶 🌽 🥕 🥔 🍠 🥐 🍞 🥨 🧀 🥚 🍳 🥞 🥓 🥩 🍗 🍖 🌭 🍔 🍟 🍕 🥪 🌮 🌯 🥗 🥘 🍝 🍜 🍲 🍛 🍣 🍱 🍤 🍙 🍚 🍘 🍢 🍡 🍧 🍨 🍦 🍰 🎂 🍮 🍭 🍬 🍫 🍩 🍪 🌰 🥜 🍯 🥛 🍼 ☕ 🍵 🍶 🍾 🍷 🍸 🍹 🍺 🍻 🥃 🥤 🥢 🍽 🍴'.split(' ') },
  activity:{ label: 'Activities', tab: '⚽', emojis: '⚽ 🏀 🏈 ⚾ 🎾 🏐 🏉 🎱 🏓 🏸 🏒 🏑 🏏 🥅 ⛳ 🏹 🎣 🥊 🥋 🎽 🛹 ⛸ 🎿 ⛷ 🏂 🏋 🤼 🤸 ⛹ 🤾 🏌 🏇 🧘 🏄 🏊 🚣 🧗 🚵 🚴 🏆 🥇 🥈 🥉 🏅 🎖 🎗 🎫 🎪 🎭 🎨 🎬 🎤 🎧 🎼 🎹 🥁 🎷 🎺 🎸 🎻 🎲 🎯 🎳 🎮 🎰 🧩 ♟'.split(' ') },
  travel:  { label: 'Travel & Places', tab: '✈', emojis: '🚗 🚕 🚙 🚌 🚎 🏎 🚓 🚑 🚒 🚐 🚚 🚛 🚜 🛴 🚲 🛵 🏍 🚨 🚔 🚍 🚘 🚖 🚡 🚠 🚟 🚃 🚋 🚞 🚝 🚄 🚅 🚈 🚂 🚆 🚇 🚊 🚉 ✈ 🛫 🛬 🛩 💺 🛰 🚀 🛸 🚁 🛶 ⛵ 🚤 🛳 ⛴ 🚢 ⚓ 🚧 ⛽ 🚏 🚦 🚥 🗺 🗽 🏰 🎡 🎢 🎠 ⛲ 🏖 🏝 🌋 🏔 🗻 🏕 🏠 🏡 🏘 🏗 🏭 🏢 🏬 🏥 🏦 🏨 🏪 🏫 ⛪ 🕌 🛤 🌅 🌄 🌠 🎇 🎆 🌇 🌆 🌃 🌌 🌉 🌁'.split(' ') },
  objects: { label: 'Objects', tab: '💡', emojis: '⌚ 📱 💻 ⌨ 🖥 🖨 🖱 💽 💾 💿 📷 📸 📹 🎥 📞 ☎ 📺 📻 ⏰ ⏳ 📡 🔋 🔌 💡 🔦 🛢 💸 💵 💴 💶 💷 💰 💳 💎 ⚖ 🔧 🔨 ⚒ 🛠 ⛏ 🔩 ⚙ ⛓ 🔫 💣 🔪 🗡 🚬 ⚰ 🏺 🔮 📿 💈 🔭 🔬 💊 💉 🩸 🌡 🧹 🧴 🛎 🔑 🚪 🛋 🛏 🧸 🖼 🛍 🛒 🎁 🎈 🎏 🎀 🎊 🎉 🎎 🏮 🎐 ✉ 📩 📨 📧 💌 📥 📤 📦 🏷 📪 📫 📬 📭 📮 📜 📄 📑 📊 📈 📉 📅 📆 🗓 📋 📁 📂 📰 📓 📔 📒 📕 📗 📘 📙 📚 📖 🔖 🔗 📎 📐 📏 📌 📍 ✂ 🖊 🖋 ✒ 🖌 🖍 📝 ✏ 🔍 🔎 🔒 🔓 🔏 🔐'.split(' ') },
  symbols: { label: 'Symbols', tab: '❤', emojis: '❤ 🧡 💛 💚 💙 💜 🖤 💔 ❣ 💕 💞 💓 💗 💖 💘 💝 💟 ☮ ✝ ☪ 🕉 ☸ ✡ 🔯 ☯ ☦ 🛐 ⛎ ♈ ♉ ♊ ♋ ♌ ♍ ♎ ♏ ♐ ♑ ♒ ♓ 🆔 ⚛ ☢ ☣ ❌ ⭕ 🛑 ⛔ 📛 🚫 💯 💢 ♨ 🔞 ❗ ❓ ❕ ❔ ‼ ⁉ ⚠ 🚸 🔱 ⚜ 🔰 ♻ ✅ ❇ ✳ ❎ 🌐 💠 🌀 💤 🏧 ♿ 🅿 🚹 🚺 🚼 🚻 🚮 🎦 📶 🔣 ℹ 🔤 🔡 🔠 🆖 🆗 🆙 🆒 🆕 🆓 🔢 ▶ ⏸ ⏹ ⏺ ⏭ ⏮ ⏩ ⏪ ◀ 🔼 🔽 ➡ ⬅ ⬆ ⬇ ↗ ↘ ↙ ↖ ↕ ↔ ↪ ↩ 🔀 🔁 🔂 🔄 🎵 🎶 ➕ ➖ ➗ ✖ 💲 💱 ™ © ® 〰 🔚 🔙 🔛 🔝 🔜 ✔ ☑ 🔘 🔴 🟠 🟡 🟢 🔵 🟣 ⚫ ⚪ 🟤 🔺 🔻 🔸 🔹 🔶 🔷 🔳 🔲 ⬛ ⬜ 🔈 🔇 🔉 🔊 🔔 🔕 📣 📢 💬 💭 ♠ ♣ ♥ ♦ 🃏 🎴 🀄 🕐 🕑 🕒 🕓 🕔 🕕 🕖 🕗 🕘 🕙 🕚 🕛'.split(' ') },
};

const CAT_KEYS = Object.keys(CATEGORIES) as Array<keyof typeof CATEGORIES>;

interface EmojiPickerProps {
  value?: string | null;
  onChange: (emoji: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'button' | 'avatar';
  color?: string;
  disabled?: boolean;
  className?: string;
}

export function EmojiPicker({
  value, onChange, placeholder = '😊',
  size = 'md', variant = 'button',
  color = '#196287', disabled = false, className,
}: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [activeCat, setActiveCat] = useState<keyof typeof CATEGORIES>('smileys');
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [open]);

  const sizeClass = {
    sm: variant === 'avatar' ? 'w-10 h-10 text-xl' : 'w-9 h-9 text-lg',
    md: variant === 'avatar' ? 'w-14 h-14 text-3xl' : 'w-11 h-11 text-2xl',
    lg: variant === 'avatar' ? 'w-20 h-20 text-5xl' : 'w-14 h-14 text-3xl',
  }[size];

  const visibleEmojis = useMemo(() => {
    if (search.trim()) {
      const all = Object.values(CATEGORIES).flatMap(c => c.emojis);
      return [...new Set(all)];
    }
    return CATEGORIES[activeCat].emojis;
  }, [search, activeCat]);

  const handlePick = (e: string) => {
    onChange(e);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={ref} className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={cn(
          'flex items-center justify-center transition-all flex-shrink-0',
          sizeClass,
          variant === 'avatar' ? 'rounded-full border-2 border-transparent hover:border-foreground/20' : 'rounded-lg border-2 border-border hover:border-sky-500',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        style={variant === 'avatar' ? { background: color + '20' } : undefined}
      >
        {value ? <span>{value}</span> : (variant === 'avatar' ? <span>{placeholder}</span> : <Smile className="w-4 h-4 text-muted-foreground" />)}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-card text-foreground rounded-2xl border shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div className="text-sm font-semibold">Pick an emoji</div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded hover:bg-muted flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-3 py-2 border-b flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <input
                autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search emoji…"
                className="flex-1 bg-transparent text-sm outline-none"
              />
              {search && (<button onClick={() => setSearch('')}><X className="w-4 h-4 text-muted-foreground" /></button>)}
            </div>
            {!search && (
              <div className="flex border-b overflow-x-auto flex-shrink-0 bg-muted/30">
                {CAT_KEYS.map(k => (
                  <button
                    key={k} type="button"
                    onClick={() => setActiveCat(k)}
                    className={cn(
                      'flex-shrink-0 w-12 h-12 text-2xl flex items-center justify-center hover:bg-muted transition',
                      activeCat === k && 'bg-sky-500/10 border-b-2 border-sky-500',
                    )}
                    title={CATEGORIES[k].label}
                  >
                    {CATEGORIES[k].tab}
                  </button>
                ))}
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-2">
              <div className="grid grid-cols-8 gap-0.5">
                {visibleEmojis.map((e, i) => (
                  <button
                    key={i} type="button" onClick={() => handlePick(e)}
                    className="aspect-square text-2xl rounded hover:bg-muted active:scale-90 transition flex items-center justify-center"
                  >
                    {e}
                  </button>
                ))}
                {visibleEmojis.length === 0 && (
                  <div className="col-span-8 py-8 text-center text-xs text-muted-foreground">No emoji found</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmojiPicker;
